<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToCascader'
});
</script>
<template>
	<div ref="$el" :key="langKey" v-tip="changed ? t('to.common.originalValue', getLabelPath(valueStore[0], data_, [], [], false)) : ''" :flex="flex ? flex : null" class="to-cascader" :class="setClass" :style="setStyle" @click="handleClick">
		<span v-if="validateRequired" v-show="false" class="to-validate" :req="validateRequired" :desc="validateDesc" :value="value_ ? 'true' : ''"></span>
		<span v-if="$slots.prefix" class="to-cascader-prefix">
			<slot name="prefix" />
		</span>
		<span v-if="props.prefix" class="to-cascader-prefix-text">
			<span>{{ props.prefix }}</span>
		</span>
		<span class="to-cascader-content">
			<span class="to-cascader-value">
				<span v-if="value_">{{ valueLabel }}</span>
				<span v-else class="to-cascader-placeholder">{{ placeholder || t('to.cascader.placeholder') }}</span>
			</span>
		</span>
		<span v-if="clearable && value_" class="to-cascader-clear">
			<to-icon value="close" @click.stop="handleClear"></to-icon>
		</span>
		<span class="to-cascader-arrow">
			<to-icon v-if="false" :value="iconValue"></to-icon>
		</span>
		<Teleport to="body">
			<div class="to-cascader" :class="setClass">
				<to-float ref="cascaderFloat" :global="false" class="to-cascader-layer" :width="floatWidth" lazy align="left" @closed="afterClose">
					<div v-if="!data_.length" style="text-align: center; color: #ccc; padding: 3em 0.3em">{{ t('to.cascader.noData') }}</div>
					<to-scroll @click.stop>
						<div class="to-cascader-options">
							<to-cascader-item v-for="(levelData, levelIndex) in optionLevels" :key="levelIndex" :data="levelData" @select="handleItemSelect"></to-cascader-item>
						</div>
					</to-scroll>
				</to-float>
			</div>
		</Teleport>
		<span v-if="errorTip" v-color="'danger'" class="to-box-tip">
			<template v-if="errorType === 'rule'">
				{{ t('to.cascader.inputError') }}
				<to-icon v-tip.danger="errorTip" style="height: auto" value="help" color="danger" mode="toast"></to-icon>
			</template>
			<template v-else> {{ t('to.cascader.required') }} </template>
		</span>
	</div>
</template>

<script setup lang="ts">
import { CSSProperties, computed, provide, ref, watch, nextTick, inject } from 'vue'
import { ToIcon, ToFloat } from '@touchvue/components'
import ToCascaderItem from './cascader-item.vue'
import { deepClone } from '@touchvue/utils'
import { t, langKey } from '@touchvue/locale'



const ToFormItem = inject<{ required?: boolean; label?: string }>('ToFormItem', {})

export type VType = string | unknown[]

export interface Item {
	label?: string
	value?: string
	children?: string
	isSelect?: boolean
	isLeaf?: boolean
	level?: number
	[key: string]: any
}

export interface Props {
	mode?: string
	lazy?: boolean
	disabled?: boolean
	any?: boolean
	clearable?: boolean
	modelValue?: string | []
	width?: string | number
	labelText?: string
	valueText?: string
	childrenText?: string
	placeholder?: string
	optionWidth?: string | number
	data: []
	comparable?: boolean
	required?: boolean
	desc?: string
	prefix?: string
	showAllLevels?: boolean
	checkedDatas?: any[]
	flex?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	mode: 'default',
	lazy: false,
	disabled: false,
	any: true,
	clearable: false,
	modelValue: '',
	width: '',
	labelText: 'label',
	valueText: 'value',
	childrenText: 'children',
	placeholder: '',
	optionWidth: '',
	data: () => [],
	comparable: false,
	required: false,
	desc: '',
	prefix: '',
	showAllLevels: true,
	checkedDatas: () => [],
	flex: false
})

const ToForm = inject<{ comparable?: boolean }>('ToForm', {})

const iconValue = ref('unfold')
const value_ = ref<VType>('')
const valueLabel = ref('')
const data_ = ref<Item[]>(initData(props.data))
const cascaderFloat = ref(null)
const $el = ref(null)
const currentLevel = ref(0)
const checkedData = ref<Item[]>([])
const optionLevels = ref<Item[][]>([data_.value])
const valueStore = ref<string[]>([])
const disabled = ref(props.disabled)
const errorTip = ref('')
const errorType = ref('rule')

const floatWidth = computed(() => {
	let unit = 'em'
	let sArr = []
	let omit = 16
	if (props.optionWidth) {
		if (isNaN(Number(props.optionWidth))) {
			if ((props.optionWidth as string).includes('em')) {
				unit = 'em'
				sArr = (props.optionWidth as string).split('em')
			} else {
				unit = 'px'
				sArr = (props.optionWidth as string).split('px')
			}
			omit = Number(sArr[0])
		} else {
			omit = Number(props.optionWidth)
		}
		return `${(currentLevel.value + 1) * omit}${unit}`
	} else {
		return ''
	}
})

const setClass = computed(() => {
	const arr = ['to-disable-target']
	if (errorTip.value) {
		arr.push('to-cascader-mode-line')
		arr.push('to-cascader-color-danger')
		arr.push('to-cascader-tip-show')
	} else {
		if (props.mode) {
			arr.push(`mode-${props.mode}`)
		}
		if ((ToForm && ToForm?.comparable) || props.comparable) {
			arr.push('to-compare')
		}
		if (changed.value) {
			arr.push('to-cascader-changed')
		}
		if (disabled.value || props.disabled) {
			arr.push('is-disabled')
		}
		if (!value_.value) {
			arr.push('is-placeholder')
		}
	}
	return arr
})
const setStyle = computed(() => {
	let obj: CSSProperties = {}
	if (props.width) {
		if (isNaN(Number(props.width))) {
			obj.width = props.width
		} else {
			obj.width = `${props.width}em`
		}
	}
	return obj
})
const changed = computed(() => {
	if ((props.comparable || ToForm?.comparable) && valueStore.value.length > 0 && value_.value !== valueStore.value[0]) {
		return true
	} else {
		return false
	}
})

const validateRequired = computed(() => {
	if (props.required) {
		return 'true'
	} else if (ToFormItem && ToFormItem.required) {
		return 'true'
	} else {
		return 'false'
	}
})
const validateDesc = computed(() => {
	if (props.desc) {
		return props.desc
	} else if (ToFormItem && ToFormItem.label) {
		return ToFormItem.label
	} else {
		return ''
	}
})
export interface ProvideProps {
	props: Props
}

provide<ProvideProps>('pProps', {
	props
})

watch(
	() => props.modelValue,
	val => {
		value_.value = val
		if (props.data) {
			nextTick(() => {
				setLabel()
			})
		}
	},
	{ immediate: true }
)
watch(
	() => props.data,
	val => {
		data_.value = initData(val)
		checkedData.value = []
		optionLevels.value = [data_.value]
		setLabel()
	},
	{ deep: true }
)

const emit = defineEmits(['change', 'update:modelValue', 'update:checkedDatas'])
function initData(data: Item[], levels = 0) {
	let arr = deepClone(data)
	let tem = []
	let level = levels
	arr.forEach((item: Item) => {
		if (item[props.childrenText] && item[props.childrenText].length) {
			tem.push({
				...item,
				level,
				isLeaf: false,
				isSelect: false,
				[props.childrenText]: initData(item[props.childrenText], level + 1)
			})
		} else {
			tem.push({ ...item, level, isSelect: false, isLeaf: true })
		}
	})
	return tem
}
function handleClick() {
	errorTip.value = ''
	if (!(props.disabled || disabled.value)) {
		if (iconValue.value === 'unfold') {
			setOptionLevelsByCheckedData()
		}
		cascaderFloat.value.toggle($el.value)
		iconValue.value = iconValue.value === 'unfold' ? 'fold' : 'unfold'
	}
}
function handleClear() {
	if (!props.disabled) {
		valueLabel.value = ''
		resetCheckedData(data_.value)
		checkedData.value = []
		optionLevels.value = [data_.value]
		value_.value = ''
		change('')
	}
}
function setLevel(idx: number) {
	currentLevel.value = idx
}
function setParentCheckedByValue(value, level) {
	fixStore(data_.value, value, level)
}
function getChildren(item: Item) {
	const children = item[props.childrenText]
	return Array.isArray(children) ? children : []
}
function setOptionLevelsByCheckedData() {
	const levels: Item[][] = [data_.value]
	let currentData = data_.value
	for (const item of checkedData.value) {
		const current = currentData.find(k => k[props.valueText] === item[props.valueText])
		if (!current) break
		const children = getChildren(current)
		if (!children.length) break
		levels.push(children)
		currentData = children
	}
	optionLevels.value = levels
	setLevel(levels.length - 1)
}
function handleItemSelect(item: Item) {
	const level = item.level || 0
	const children = getChildren(item)

	optionLevels.value = optionLevels.value.slice(0, level + 1)
	setParentCheckedByValue(item[props.valueText], level)
	if (children.length) {
		optionLevels.value = [...optionLevels.value, children]
		setLevel(level + 1)
		if (props.any) {
			setViewData()
		}
	} else {
		setLevel(level)
		setViewData()
		close()
	}
}
function fixStore(arr, value, level) {
	arr.forEach((item: Item) => {
		if (item.level === level) {
			if (item[props.valueText] === value) {
				item.isSelect = true
				setCheckedData(checkedData.value, level, item)
			} else {
				item.isSelect = false
			}
		} else {
			if (item[props.childrenText] && item[props.childrenText].length) {
				fixStore(item[props.childrenText], value, level)
			}
		}
	})
}
function setCheckedData(arr: Item[], level, item) {
	let idx = arr.findIndex(k => k.level === level)
	if (arr.length && idx !== -1) {
		if (arr[idx][props.valueText] !== item[props.valueText]) {
			let resetArr = arr.filter(k => k.level > level)
			if (resetArr.length) {
				resetArr.forEach(k => {
					let i = arr.findIndex(l => l.level === k.level)
					if (i !== -1) {
						arr.splice(i, 1)
					}
				})
			}
		}
	}
	if (idx !== -1) {
		arr[idx] = { ...item }
	} else {
		arr.push(deepClone(item))
	}
}
function resetCheckedData(arr: Item[]) {
	arr.forEach((item: Item) => {
		item.isSelect = false
		if (item[props.childrenText] && item[props.childrenText].length) {
			resetCheckedData(item[props.childrenText])
		}
	})
}
function setViewData() {
	let labelArr = checkedData.value.map(item => item[props.labelText])
	let leafArr = checkedData.value.map(item => item.isLeaf)
	if (props.showAllLevels) {
		valueLabel.value = labelArr.join(' / ')
	} else {
		if (leafArr[leafArr.length - 1]) {
			valueLabel.value = labelArr[labelArr.length - 1]
		} else {
			valueLabel.value = ''
		}
	}
	let leafItem = []
	if (!props.any) {
		leafItem = checkedData.value.filter(item => item.isLeaf)
	} else {
		leafItem = checkedData.value.length ? [checkedData.value[checkedData.value.length - 1]] : []
	}
	if (leafItem.length) {
		value_.value = leafItem[0][props.valueText]
		change(value_.value)
	}
}
function close() {
	cascaderFloat.value.close()
}
function afterClose() {
	setLevel(0)
	iconValue.value = 'unfold'
}
function change(v) {
	emit('update:modelValue', v)
	emit('change', v, checkedData.value)
}
const setLabel = () => {
	resetCheckedData(data_.value)
	if (value_.value) {
		valueLabel.value = getLabelPath(value_.value, data_.value) || value_.value
		checkedData.value.forEach(item => {
			item.isSelect = true
		})
	} else {
		checkedData.value = []
		valueLabel.value = props.placeholder
	}
	setOptionLevelsByCheckedData()
}

// 添加获取标签路径的函数
function getLabelPath(value, data, path = [], pData = [], syncCheckedData = true) {
	for (const item of data) {
		if (item[props.valueText] === value) {
			let tem: any = []
			if (syncCheckedData) {
				checkedData.value = [...pData, item]
			}
			if (props.showAllLevels) {
				tem = [...path, item[props.labelText]].join(' / ')
			} else {
				if (item.isLeaf) {
					tem = item[props.labelText]
				} else {
					tem = ''
				}
			}
			return tem
		}

		if (item[props.childrenText] && item[props.childrenText].length) {
			const result = getLabelPath(value, item[props.childrenText], [...path, item[props.labelText]], [...pData, item], syncCheckedData)
			if (result) {
				return result
			}
		}
	}
	return ''
}
function compare() {
	valueStore.value.unshift(String(value_.value))
}
// 撤销
function restore() {
	value_.value = valueStore.value[0]
	valueStore.value.splice(0, 1)
}
const setDisabled = (f: boolean) => {
	disabled.value = f
}

const getCheckData = () => {
	return checkedData.value
}
watch(
	() => checkedData.value,
	val => {
		emit('update:checkedDatas', val)
	}
)

defineExpose({
	compare,
	restore,
	changed,
	setDisabled,
	errorTip,
	errorType,
	getCheckData
})
</script>
